import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx'

import type { FirebaseRepository } from '../models/FirebaseRepository'
import { type StaticModelCollection } from '../types'
import { Cubit } from './core'
import { Section } from '../models/Section'
import { fetchSectionsByIds } from '../firestore/Section'
import { SectionAssignment } from '../models/SectionAssignment'
import { fetchSectionAssignmentsByIds } from '../firestore/SectionAssignment'
import { SlideDeck } from '../models/SlideDeck'
import { fetchSlideDecksById } from '../firestore/SlideDeck'
import { RoomState } from '../models/RoomState'
import { fetchRoomStatesByIds, forceAIProcessing } from '../firestore/RoomState'
import { getUnsuccessfulAndRecentAIRuns } from '../firestore/RoomStateAI'
import { RoomStateAI } from '../models/RoomStateAI'

export class AdminDashboardAIAssessmentsCubit extends Cubit {
  repository: FirebaseRepository

  private aiRuns: StaticModelCollection<RoomStateAI>
  private invalidRoomIds = observable.set<string>()

  @observable sections = observable.map<string, Section>()
  @observable assignments = observable.map<string, SectionAssignment>()
  @observable slideDecks = observable.map<string, SlideDeck>()
  @observable roomStates = observable.map<string, RoomState>()
  @observable sortingStrategy: {
    column: 'slideDeck' | 'roomName' | 'className' | 'roomScheduled' | 'aiStart'
    direction: 'asc' | 'desc'
  } = { direction: 'desc', column: 'aiStart' }

  private aiAndDependentDataInitialized = observable.box(false)

  constructor(repository: FirebaseRepository) {
    super()
    this.repository = repository
    this.aiRuns = RoomStateAI.emptyCollection(this.repository)
    makeObservable(this)
  }

  initialize(): void {
    // start the conversation items stream
    this.addStream(getUnsuccessfulAndRecentAIRuns(this.repository), (models) =>
      this.aiRuns.replaceModels(models)
    )

    this.addReaction({
      whenThisChanges: () => ({
        relevantRoomIds: Array.from(
          this.aiRunsToFetchDependentDataForByRoomId.keys()
        ),
      }),
      thenRunThisCode: ({ relevantRoomIds }) => {
        this.fetchRoomStatesAndDependentData(relevantRoomIds)
      },
    })

    // check keys to run reaction whenever any of the maps change
    const disposer = reaction(
      () => ({
        aiRunsLoaded: this.aiRuns.isLoaded,
        relevantRoomIds: Array.from(
          this.aiRunsToFetchDependentDataForByRoomId.keys()
        ),
        sections: Array.from(this.sections.entries()),
        assignments: Array.from(this.assignments.entries()),
        slideDecks: Array.from(this.slideDecks.entries()),
        roomStates: Array.from(this.roomStates.entries()),
      }),
      ({ relevantRoomIds }) => {
        if (!this.aiRuns.isLoaded) return // wait for ai runs to load
        if (!this.aiRuns.length)
          return this.aiAndDependentDataInitialized.set(true) // no runs, so we are done and loaded

        // check if aiRunsWithRoomIds all have a valid room state
        const aiRunsHaveRoomStates = relevantRoomIds.every((roomId) => {
          if (!roomId) return true // if no roomId, can't fetch room state, but don't block loading
          const roomState = this.roomStates.get(roomId)
          return roomState && roomState.isNotEmpty
        })

        if (!aiRunsHaveRoomStates) return

        // check if all room state have loaded their deps
        const roomStates = Array.from(this.roomStates.values())
        const allRoomStatesHaveDeps = roomStates.every((roomState) =>
          this.roomHasLoadedDependencies(roomState)
        )
        if (!allRoomStatesHaveDeps) return // still waiting
        this.aiAndDependentDataInitialized.set(true) // all deps loaded, we are done
        disposer() // dispose self as this only needs to complete once for initial render
      }
    )

    this.disposers.push(disposer)
  }

  private roomHasLoadedDependencies(roomState: RoomState) {
    if (roomState.isEmpty) return false // didn't load room, can't determine dependency state
    const { sectionId, assignmentId, slideDeckId } = roomState.data
    if (!sectionId || !assignmentId || !slideDeckId) return true // not going to load deps for this room
    const section = this.sections.get(sectionId)
    const assignment = this.assignments.get(
      getSectionAssignmentKey({ sectionId, assignmentId })
    )
    const slideDeck = this.slideDecks.get(slideDeckId)
    return [section, assignment, slideDeck].every(
      (model) => model && !model.isEmpty
    )
  }

  processAIAgainForRoomId = async (roomId: string) => {
    if (!roomId) return
    const roomState = this.roomStates.get(roomId)
    if (!roomState || roomState.isEmpty) return
    return await forceAIProcessing(this.repository, { roomId })
  }

  @action
  changeSortingStrategy = (newStrategy: typeof this.sortingStrategy) => {
    this.sortingStrategy = newStrategy
  }

  @computed
  get processedAIRunsWithinLastWeek() {
    const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
    return this.aiRuns.models.filter((ai) => {
      const endedAt = ai.data.endedAt as Date | null
      return endedAt && endedAt.getTime() >= oneWeekAgo
    }).length
  }

  @computed
  get sortedRoomRowsByAIStatus() {
    return {
      ready: this.roomRowsByAIStatus.ready,
      succeededWithinHour: this.sortRowsFromStrategy(
        this.roomRowsByAIStatus.succeededWithinHour
      ).slice(),
      failed: this.sortRowsFromStrategy(this.roomRowsByAIStatus.failed).slice(),
      running: this.sortRowsFromStrategy(
        this.roomRowsByAIStatus.running
      ).slice(),
    }
  }

  @computed
  private get aiRunsToFetchDependentDataForByRoomId() {
    const aiRunsByRoomId = new Map<string, RoomStateAI[]>()
    this.aiRuns.models.forEach((ai) => {
      const { roomId } = ai.data
      if (!roomId) return
      const existing = aiRunsByRoomId.get(roomId)
      if (existing) {
        existing.push(ai)
      } else {
        aiRunsByRoomId.set(roomId, [ai])
      }
    })

    // iterate over each map entry, and categorize the room based on its ai runs
    // if does not meet a category, do not include it in the returned map
    const categorizedMap = new Map<
      string,
      { category: AIRunsCategory; aiRuns: RoomStateAI[] }
    >()
    for (const [roomId, aiRuns] of aiRunsByRoomId) {
      const category = getCategoryForAIRuns(aiRuns)
      if (!category) continue
      categorizedMap.set(roomId, { category, aiRuns })
    }
    return categorizedMap
  }

  @computed
  private get roomRowsByAIStatus() {
    type AssessmentRow = {
      aiAssessments: RoomStateAI[]
      roomState: RoomState
      section: Section
      assignment: SectionAssignment
      slideDeck: SlideDeck
      category: AIRunsCategory
      earliestStartedAt: Date
    }

    const succeededWithinHour: AssessmentRow[] = []
    const failed: AssessmentRow[] = []
    const running: AssessmentRow[] = []

    Array.from(this.aiRunsToFetchDependentDataForByRoomId.entries()).forEach(
      ([roomId, { category, aiRuns }]) => {
        if (!roomId) return
        const roomState = this.roomStates.get(roomId)
        if (!roomState || roomState.isEmpty) return
        const { sectionId, assignmentId, slideDeckId } = roomState.data

        if (!sectionId || !assignmentId || !slideDeckId) return
        const section = this.sections.get(sectionId)
        if (!section || section.isEmpty) return
        const assignment = this.assignments.get(
          getSectionAssignmentKey({ sectionId, assignmentId })
        )
        if (!assignment || assignment.isEmpty) return
        const slideDeck = this.slideDecks.get(slideDeckId)
        if (!slideDeck || slideDeck.isEmpty) return

        const earliestStartedAt = aiRuns.reduce<Date | null>((earliest, ai) => {
          const startedAt = ai.data.startedAt as Date
          if (!earliest || startedAt < earliest) return startedAt
          return earliest
        }, null)

        if (!earliestStartedAt) return

        const row = {
          aiAssessments: aiRuns,
          roomState,
          section,
          assignment,
          slideDeck,
          category,
          earliestStartedAt,
        }

        switch (category) {
          case 'succeededWithinHour':
            succeededWithinHour.push(row)
            break
          case 'failed':
            failed.push(row)
            break
          case 'running':
            running.push(row)
            break
        }
      }
    )
    return {
      ready: this.aiAndDependentDataInitialized.get(),
      succeededWithinHour,
      failed,
      running,
    }
  }

  private fetchRoomStatesAndDependentData = async (roomIds: string[]) => {
    const roomIdsToFetch = roomIds.filter(
      (id) => id && !this.roomStates.has(id)
    )

    runInAction(() =>
      roomIdsToFetch.forEach((id) =>
        this.roomStates.set(id, RoomState.empty(this.repository))
      )
    )
    const roomStates = await fetchRoomStatesByIds(
      this.repository,
      roomIdsToFetch
    )

    runInAction(() => {
      roomStates.forEach((roomState) =>
        this.roomStates.set(roomState.id, roomState)
      )
    })

    const sectionIds = new Set<string>()
    const assignmentKeys = new Set<string>()
    const slideDeckIds = new Set<string>()

    for (const roomState of roomStates) {
      const { sectionId, assignmentId, slideDeckId } = roomState.data
      if (!sectionId || !assignmentId || !slideDeckId) {
        runInAction(() => this.invalidRoomIds.add(roomState.id))
        continue
      }

      sectionIds.add(sectionId)
      assignmentKeys.add(getSectionAssignmentKey({ sectionId, assignmentId }))
      slideDeckIds.add(slideDeckId)
    }

    this.fetchSections(Array.from(sectionIds))
    this.fetchSlideDecks(Array.from(slideDeckIds))
    this.fetchAssignments(
      Array.from(assignmentKeys).map((key) => {
        const [sectionId, assignmentId] = key.split('-')
        return { sectionId, assignmentId }
      })
    )
  }

  private fetchSections = async (sectionIds: string[]) => {
    const sectionsIdsToFetch = sectionIds.filter(
      (id) => id && !this.sections.has(id)
    )

    runInAction(() =>
      sectionsIdsToFetch.forEach((id) =>
        this.sections.set(id, Section.empty(this.repository))
      )
    )
    const sections = await fetchSectionsByIds(
      this.repository,
      sectionsIdsToFetch
    )

    runInAction(() => {
      sections.forEach((section) => this.sections.set(section.id, section))
    })
  }

  private fetchSlideDecks = async (slideDeckIds: string[]) => {
    const slideDecksIdsToFetch = slideDeckIds.filter(
      (id) => id && !this.slideDecks.has(id)
    )
    runInAction(() =>
      slideDecksIdsToFetch.forEach((id) =>
        this.slideDecks.set(id, SlideDeck.empty(this.repository))
      )
    )
    const slideDecks = await fetchSlideDecksById(this.repository, {
      slideDeckIds: slideDecksIdsToFetch,
    })
    runInAction(() => {
      slideDecks.forEach((slideDeck) =>
        this.slideDecks.set(slideDeck.id, slideDeck)
      )
    })
  }

  private fetchAssignments = async (
    keys: { sectionId: string; assignmentId: string }[]
  ) => {
    const keysToFetch = keys.filter(
      ({ sectionId, assignmentId }) =>
        !!sectionId &&
        !!assignmentId &&
        !this.assignments.has(
          getSectionAssignmentKey({ sectionId, assignmentId })
        )
    )
    runInAction(() =>
      keysToFetch.forEach(({ sectionId, assignmentId }) =>
        this.assignments.set(
          getSectionAssignmentKey({ sectionId, assignmentId }),
          SectionAssignment.empty(this.repository)
        )
      )
    )
    const assignments = await fetchSectionAssignmentsByIds(this.repository, {
      ids: keysToFetch,
    })
    runInAction(() => {
      assignments.forEach((assignment) =>
        this.assignments.set(
          getSectionAssignmentKey({
            sectionId: assignment.sectionId,
            assignmentId: assignment.id,
          }),
          assignment
        )
      )
    })
    return assignments
  }

  private sortRowsFromStrategy = (
    rows: typeof this.roomRowsByAIStatus.succeededWithinHour
  ) => {
    return rows.sort((aRow, bRow) => {
      const [a, b]: [number, number] | [string, string] = (() => {
        switch (this.sortingStrategy?.column) {
          case 'slideDeck':
            return [
              aRow.slideDeck.data.slideDeckName ?? '',
              bRow.slideDeck.data.slideDeckName ?? '',
            ]
          case 'roomName':
            return [
              aRow.roomState.simpleRoomName ?? '',
              bRow.roomState.simpleRoomName ?? '',
            ]
          case 'className':
            return [
              aRow.section.data.className ?? '',
              bRow.section.data.className ?? '',
            ]
          case 'roomScheduled':
            return [
              aRow.roomState.data.scheduledAt?.getTime() ?? 0,
              bRow.roomState.data.scheduledAt?.getTime() ?? 0,
            ]
          case 'aiStart':
            return [
              aRow.earliestStartedAt.getTime(),
              bRow.earliestStartedAt.getTime(),
            ]
        }
      })()

      const first = this.sortingStrategy?.direction === 'asc' ? a : b
      const second = this.sortingStrategy?.direction === 'asc' ? b : a

      return typeof first === 'string'
        ? first
            .toLowerCase()
            .localeCompare((second as string).toLowerCase(), undefined, {
              numeric: true,
              sensitivity: 'base',
            })
        : first - (second as number)
    })
  }
}

function getSectionAssignmentKey({
  sectionId,
  assignmentId,
}: {
  sectionId: string
  assignmentId: string
}) {
  return `${sectionId}-${assignmentId}`
}

function getCategoryForAIRuns(
  _aiRuns: RoomStateAI[]
): AIRunsCategory | undefined {
  // query will not return ai runs without startedAt
  const aiRuns = _aiRuns as (RoomStateAI & { data: { startedAt: Date } })[]
  const oneHourAgoMS = Date.now() - 60 * 60 * 1000

  // we only care about single attempt failures
  if (
    aiRuns.length === 1 &&
    aiRuns[0].data.success === false &&
    aiRuns[0].data.startedAt.getTime() < oneHourAgoMS
  ) {
    return 'failed'
  }

  if (aiRuns.length < 2) return undefined

  const mostRecentSuccess = aiRuns.reduce<RoomStateAI | null>(
    (mostRecent, ai) => {
      if (!ai.data.success) return mostRecent
      const currentStartedAt = ai.data.startedAt
      const mostRecentStartedAt = mostRecent?.data.startedAt
      return !mostRecentStartedAt || currentStartedAt > mostRecentStartedAt
        ? ai
        : mostRecent
    },
    null
  )

  const succeededWithinHour =
    mostRecentSuccess?.data.startedAt &&
    mostRecentSuccess.data.startedAt.getTime() > oneHourAgoMS

  if (mostRecentSuccess) {
    if (succeededWithinHour) return 'succeededWithinHour'
    return undefined // succeeded over an hour ago, don't care about this run
  }

  if (aiRuns.some((ai) => !ai.data.endedAt)) return 'running' // no successes, but has running ai
  return 'failed' // no successes, no running, so failed
}

type AIRunsCategory = 'succeededWithinHour' | 'failed' | 'running'
